import React, { useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  Button,
  message,
  DatePicker,
  Select,
  InputNumber,
  Upload,
  Modal,
  Row,
  Col,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import API from "../../api";
import moment from "moment";
import { usePDFSlick, PDFSlickViewer } from "@pdfslick/react"; // Correct imports
import "@pdfslick/react/dist/pdf_viewer.css"; // Import required CSS
import { getUserRole } from "../../utils/auth";

const { Option } = Select;

const ExpansesDrawer = ({ open, setOpen, data, isEdit, onSubmit, stores }) => {
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const [existingBill, setExistingBill] = useState("");
  const [expanseHeads, setExpanseHeads] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null); // State for PDF preview URL
  const [pdfModalVisible, setPdfModalVisible] = useState(false); // State for modal visibility
  const userId = JSON.parse(localStorage.getItem("userData"))?.id; // Get user ID from localStorage
  const userRole = JSON.parse(localStorage.getItem("userData"))?.role; // Get user ID from localStorage
  const userStore = JSON.parse(localStorage.getItem("userData"))?.storeId; // Get user ID from localStorage

  // Use the usePDFSlick hook for PDF rendering
  const {
    viewerRef,
    usePDFSlickStore,
    PDFSlickViewer: ViewerComponent,
  } = usePDFSlick(
    pdfPreviewUrl || "", // Pass the PDF URL when available
    { singlePageViewer: false } // Options for the viewer
  );

  const defaultData = {
    date: null,
    expanseHead: null,
    amount: 0,
    paymentMethod: null,
    bill: "",
    description: "",
    store: "",
    billCreatedBy: userId || "", // Default to user ID
  };

  const fetchExpanseHeads = async () => {
    try {
      const response = await API.get("/expanse-heads");
      setExpanseHeads(response.data || []);
    } catch (err) {
      console.error("Error fetching expanse heads:", err);
      message.error("Failed to load expense heads.");
    }
  };

  useEffect(() => {
    fetchExpanseHeads();
  }, []);



  useEffect(() => {
    if (open) {
      const store = userRole.role !== 'admin' ? stores.find(store => userStore === store._id) : null; 
      if (isEdit && data) {
        console.log("Edit mode data:", data);
        const billValue = data.bill || "";
        setExistingBill(billValue);
        const expanseHeadId = data.expanseHead?._id || null;
        form.setFieldsValue({
          date: data.date ? moment(data.date) : null,
          expanseHead: expanseHeadId,
          amount: data.amount,
          paymentMethod: data.paymentMethod,
          bill: billValue,
          description: data.description,
          store: data.store || store.name || "",
          billCreatedBy: data.billCreatedBy || userId || "",
        });
        setFileList(
          billValue
            ? [
              {
                uid: "-1",
                name: "Existing Bill",
                status: "done",
                url: billValue,
              },
            ]
            : []
        );
      } else {
        form.resetFields();
        setFileList([]);
        setExistingBill("");
        form.setFieldsValue({
          billCreatedBy: userId || "",
          store: userRole !== 'admin' ? store?.name || '' : '',
        });
      }
    }
  }, [open, isEdit, data, form, userId]);

  const customRequest = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);

    if (existingBill && existingBill.trim() !== "") {
      formData.append("existingFilePath", existingBill);
    }

    try {
      const response = await API.post("/expanse/upload-bill", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filePath = response.data.filePath;
      form.setFieldsValue({ bill: filePath });
      setExistingBill(filePath);
      setFileList([
        {
          uid: "-1",
          name: file.name,
          status: "done",
          url: filePath,
        },
      ]);
      onSuccess(response.data);
      message.success(`${file.name} uploaded successfully`);
    } catch (error) {
      console.error("Error uploading file:", error);
      onError(error);
      message.error("Failed to upload bill.");
    }
  };

  const handleFileRemove = async (file) => {
    const filePath =
      file.response?.filePath ||
      file.url ||
      form.getFieldValue("bill") ||
      existingBill;

    if (!filePath) {
      setFileList([]);
      setExistingBill("");
      form.setFieldsValue({ bill: "" });
      return true;
    }

    try {
      const response = await API.delete("/expanse/delete-bill", {
        data: { filePath },
      });
      message.success(
        response.data.message || "Bill deleted successfully from server."
      );
      setFileList([]);
      setExistingBill("");
      form.setFieldsValue({ bill: "" });
      return true;
    } catch (error) {
      console.error("Error deleting file from server:", error);
      message.error(
        error.response?.data?.message || "Failed to delete bill from server."
      );
      return false;
    }
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    if (newFileList.length > 0 && newFileList[0].status === "done") {
      const filePath = newFileList[0].response?.filePath || newFileList[0].url;
      form.setFieldsValue({ bill: filePath });
    } else {
      form.setFieldsValue({ bill: "" });
    }
  };

  const handlePreviewPdf = async (fileUrl) => {
    try {
      const response = await API.get(
        `/expanse/download-bill/${encodeURIComponent(fileUrl)}`,
        {
          responseType: "blob",
        }
      );
      const pdfUrl = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      setPdfPreviewUrl(pdfUrl);
      setPdfModalVisible(true);
    } catch (error) {
      console.error("Error fetching PDF for preview:", error);
      message.error("Failed to load PDF for preview.");
    }
  };

  const handleDownloadPdf = async (fileUrl) => {
    try {
      const response = await API.get(
        `/expanse/download-bill/${encodeURIComponent(fileUrl)}`,
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        fileUrl.split("/").pop() || "downloaded.pdf"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      message.error("Failed to download PDF.");
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        date: values.date ? values.date.toISOString() : null,
        bill: values.bill || existingBill || "",
        expanseHead: values.expanseHead,
        billCreatedBy: values.billCreatedBy || userId,
      };
      if (isEdit) {
        await onSubmit({ ...data, ...payload });
      } else {
        await onSubmit(payload);
      }
      form.resetFields();
      setFileList([]);
      setExistingBill("");
      setOpen(false);
      message.success(
        isEdit ? "Expense updated successfully" : "Expense added successfully"
      );
    } catch (err) {
      console.error("Error submitting form:", err);
      message.error("Failed to submit the form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={isEdit ? "Edit Expense" : "Add Expense"}
      width={800}
      placement="right"
      onClose={() => setOpen(false)}
      open={open}
      destroyOnClose
    >


      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        autoComplete="off"
        initialValues={defaultData}
      >
        <Row gutter={[5, 2]}>
          <Col xs={24} sm={12} md={12}>
            <Form.Item
              label="Date"
              name="date"
              rules={[{ required: true, message: "Please select the date." }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={12}>
            <Form.Item
              label="Expense Head"
              name="expanseHead"
              rules={[
                { required: true, message: "Please select an expense head." },
              ]}
            >
              <Select 
                placeholder="Select an expense head"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                allowClear
                style={{ width: '100%' }}
              >
                {expanseHeads.map((head) => (
                  <Option key={head._id} value={head._id}>
                    {head.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Form.Item
              label="Amount"
              name="amount"
              rules={[
                { required: true, message: "Please enter the amount." },
                {
                  type: "number",
                  min: 0,
                  message: "Amount must be a positive number.",
                },
              ]}
            >
              <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Form.Item label="Payment Method" name="paymentMethod">
              <Select placeholder="Select payment method" allowClear>
                <Option value="cash">Cash</Option>
                <Option value="upi">UPI</Option>
                <Option value="bank_transfer">Bank Transfer</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Form.Item
              label="Store"
              name="store"
              rules={[{ required: true, message: "Please select a store." }]}
            >
              <Select placeholder="Select a store" allowClear disabled={userRole !== 'admin'}>
                {stores.map((store) => (
                  <Option key={store._id} value={store.name}>
                    {store.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={24}>
            <Form.Item
              label="Created By"
              name="billCreatedBy"
              rules={[
                {
                  required: true,
                  message: "Please enter who created the bill.",
                },
              ]}
              style={{ display: "none" }}
            >
              <Input value={userId} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Form.Item label="Bill (PDF)" name="bill">
              <Upload
                customRequest={customRequest}
                accept=".pdf"
                fileList={fileList}
                onChange={handleFileChange}
                onRemove={handleFileRemove}
                maxCount={1}
                listType="text"
                onPreview={(file) =>
                  handlePreviewPdf(file.url || file.response?.filePath)
                }
                beforeUpload={(file) => {
                  const isPDF = file.type === "application/pdf";
                  if (!isPDF) {
                    message.error("You can only upload PDF files!");
                  }
                  return isPDF || Upload.LIST_IGNORE;
                }}
              >
                <Button icon={<UploadOutlined />}>Upload PDF</Button>
              </Upload>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={24}>
            <Form.Item label="Description" name="description">
              <Input.TextArea
                rows={4}
                placeholder="Enter description (optional)"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Form.Item>
              <Button
                type="primary"
                block
                htmlType="submit"
                loading={isLoading}
              >
                {isEdit ? "Update Expense" : "Add Expense"}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>



      {/* Modal for PDF Preview */}
      <Modal
        title="PDF Preview"
        visible={pdfModalVisible}
        onCancel={() => {
          setPdfModalVisible(false);
          setPdfPreviewUrl(null); // Clean up URL
        }}
        footer={[
          <Button
            key="download"
            type="primary"
            onClick={() => handleDownloadPdf(existingBill || fileList[0]?.url)}
          >
            Download PDF
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setPdfModalVisible(false);
              setPdfPreviewUrl(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={800}
      >
        {pdfPreviewUrl ? (
          <ViewerComponent
            viewerRef={viewerRef}
            usePDFSlickStore={usePDFSlickStore}
          />
        ) : (
          <p>Loading PDF...</p>
        )}
      </Modal>
    </Drawer>
  );
};

export default ExpansesDrawer;
